import { supabase } from '../../lib/supabase';
import type { Artist, ArtistContent } from '../../interfaces/maestra';

// Encapsula o acesso à tabela `artists`. RLS isola por usuário/membro.

/**
 * ⚠️ `role` NÃO TEM DEFAULT, E É DE PROPÓSITO.
 *
 * `role` não existe na tabela `artists`: ele nasce do cruzamento com `artist_members`, que só
 * `listArtists` faz. Com `= 'owner'` como default, TODO retorno de update carimbava "dono" por
 * cima do estado em memória de um colaborador — e a partir dali `perfilEstaAtivo` passava a
 * consultar o PRO da CONTA LOGADA em vez do `owner_is_pro` do dono do perfil.
 *
 * Na prática: bastava o convidado salvar uma vez para o perfil de um titular sem assinatura
 * "destravar" para ele, porque o app passava a olhar a assinatura da pessoa errada. Sem default,
 * quem não sabe o papel não afirma nenhum, e o `upsert` do slice preserva o que já sabia.
 */
const mapRow = (
  row: any,
  role?: 'owner' | 'member',
  extra?: { access_levels?: Artist['access_levels']; owner_is_pro?: boolean }
): Artist => ({
  id: row.id,
  user_id: row.user_id,
  name: row.name,
  content: (row.content as ArtistContent) || {},
  is_locked: row.is_locked,
  legacy_unlocked: row.legacy_unlocked,
  liberado_por_codigo: row.liberado_por_codigo === true,
  slot_state: row.slot_state,
  fica_na_cota: row.fica_na_cota ?? null,
  foto_url: row.foto_url ?? null,
  logo_url: row.logo_url ?? null,
  contato_comercial: row.contato_comercial ?? null,
  created_at: row.created_at,
  updated_at: row.updated_at,
  ...(role ? { role } : {}),
  ...(extra?.access_levels ? { access_levels: extra.access_levels } : {}),
  ...(extra?.owner_is_pro !== undefined ? { owner_is_pro: extra.owner_is_pro } : {}),
});

/** Lista artistas do usuário (dono) + artistas onde ele é membro ativo. */
export const listArtists = async (userId: string): Promise<Artist[]> => {
  const owned = await supabase
    .from('artists')
    .select('*')
    .eq('user_id', userId)
    .order('created_at', { ascending: false });

  if (owned.error) throw owned.error;
  const ownedArtists = (owned.data || []).map((r) => mapRow(r, 'owner'));

  // Artistas onde é membro (status active) — traz os access_levels concedidos no convite.
  const memberships = await supabase
    .from('artist_members')
    .select('artist_id, access_levels')
    .eq('user_id', userId)
    .eq('status', 'active');

  let memberArtists: Artist[] = [];
  const levelsByArtist = new Map<string, Artist['access_levels']>();
  (memberships.data || []).forEach((m: any) => levelsByArtist.set(m.artist_id, m.access_levels || []));
  const memberIds = (memberships.data || [])
    .map((m: any) => m.artist_id)
    .filter((id: string) => !ownedArtists.some((a) => a.id === id));

  if (memberIds.length) {
    // ⚠️ PERFIL COMPARTILHADO VEM POR `perfis_da_equipe`, e não por `select` em `artists` (decisão
    // D1 da auditoria da Nyta, 04/10/2026). Quem não vê o Desenvolvimento não lê mais a linha crua
    // (a RLS a esconde, porque o plano mora no `content`); a função devolve o perfil com o
    // `content` inteiro para quem vê o Desenvolvimento e reduzido para quem não vê.
    const res = await supabase.rpc('perfis_da_equipe', { ids: memberIds });
    if (res.error) throw res.error;
    // Limite de faixas é POR PERFIL: precisamos saber se o DONO é PRO (o membro não tem
    // acesso à assinatura alheia — vem por RPC SECURITY DEFINER).
    const proRes = await supabase.rpc('artists_owner_pro', { ids: memberIds });
    const proByArtist = new Map<string, boolean>();
    (proRes.data || []).forEach((r: any) => proByArtist.set(r.artist_id, !!r.owner_is_pro));
    memberArtists = ((res.data as any[]) || []).map((r) =>
      mapRow(r, 'member', {
        access_levels: levelsByArtist.get(r.id) || [],
        owner_is_pro: proByArtist.get(r.id) ?? false,
      })
    );
  }

  return [...ownedArtists, ...memberArtists];
};

export const getArtist = async (id: string): Promise<Artist | null> => {
  const { data, error } = await supabase.from('artists').select('*').eq('id', id).maybeSingle();
  if (error) throw error;
  if (data) return mapRow(data);
  // Membro sem acesso ao Desenvolvimento: a linha crua não aparece, e o perfil vem reduzido pela
  // função da equipe (ver `listArtists`).
  const daEquipe = await supabase.rpc('perfis_da_equipe', { ids: [id] });
  if (daEquipe.error) throw daEquipe.error;
  const linha = ((daEquipe.data as any[]) || [])[0];
  return linha ? mapRow(linha) : null;
};

export const createArtist = async (input: {
  userId: string;
  name: string;
  content: ArtistContent;
  spotifyArtistId?: string;
}): Promise<Artist> => {
  // Unicidade por usuário é garantida pela constraint `artists_user_spotify_unique`
  const { data, error } = await supabase
    .from('artists')
    .insert({
      user_id: input.userId,
      name: input.name,
      content: input.content,
      spotify_artist_id: input.spotifyArtistId || null,
    })
    .select('*')
    .single();
  if (error) throw error;
  // Quem cria um perfil é dono dele: aqui o papel é sabido, e afirmá-lo é correto.
  return mapRow(data, 'owner');
};

export const updateArtist = async (
  id: string,
  patch: { name?: string; content?: ArtistContent; logo_url?: string | null; contato_comercial?: Artist['contato_comercial'] }
): Promise<Artist> => {
  const { data, error } = await supabase
    .from('artists')
    .update({ ...patch, updated_at: new Date().toISOString() })
    .eq('id', id)
    .select('*')
    .single();
  if (error) throw error;
  return mapRow(data);
};

export const deleteArtist = async (id: string): Promise<void> => {
  const { error } = await supabase.from('artists').delete().eq('id', id);
  if (error) throw error;
};

/**
 * Muda UM item do plano (ação ou tarefa, pelo id) dentro de `content.strategies`, no banco
 * (`atualizar_item_do_plano`, migração 20260926150000).
 *
 * ⚠️ É O CAMINHO DE QUEM MEXE EM UM ITEM SÓ (a Agenda). Regravar o `content` inteiro a partir da
 * memória da tela apagava o que a Nyta ou outro membro tinha acabado de mudar no plano. Roda com a
 * RLS de quem chama. Devolve o `content` novo, ou nulo se o item não existe mais.
 */
export const atualizarItemDoPlano = async (
  artistId: string,
  itemId: string,
  patch: Record<string, unknown>,
  remover: string[] = [],
): Promise<ArtistContent | null> => {
  const { data, error } = await supabase.rpc('atualizar_item_do_plano', {
    p_artist_id: artistId, p_item_id: itemId, p_patch: patch, p_remover: remover,
  });
  if (error) throw error;
  return (data as ArtistContent | null) ?? null;
};
